import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ArchiveRestore, Trash2, MoreHorizontal } from 'lucide-react';
import { RootState } from '../../reducers';
import {
  loadBackupRecordsThunk,
  uploadBackupRecordThunk,
  deleteBackupRecordThunk,
} from './settingsSlice';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Separator } from '../../components/ui/separator';
import { SettingsLayout } from '../../components/ui/settings-section';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

interface BackupRecord {
  fileId: string;
  startedAt: number;
  teamId?: string;
  folderId?: string;
  uploadId?: string;
  status?: 'pending' | 'dismissed' | 'uploaded' | 'recovered'; // Optional for backwards compatibility
}

const RecoveryPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { backupRecords, recoveryState } = useSelector(
    (state: RootState) => state.settings.recovery,
  );
  const records = useMemo(() => backupRecords || [], [backupRecords]);

  const handleRecover = async (record: BackupRecord) => {
    dispatch(uploadBackupRecordThunk(record));
  };

  const handleDelete = async (fileId: string) => {
    dispatch(deleteBackupRecordThunk(fileId));
  };

  useEffect(() => {
    dispatch(loadBackupRecordsThunk());
  }, [dispatch]);

  return (
    <SettingsLayout>
      <div className="space-y-1">
        <p className="helper-text">{t('recovery.description')}</p>
      </div>

      {records.length > 0 && (
        <>
          <Separator />
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('recovery.table.headers.fileId')}</TableHead>
                  <TableHead>{t('recovery.table.headers.time')}</TableHead>
                  <TableHead>{t('recovery.table.headers.status')}</TableHead>
                  <TableHead className="text-center">
                    {t('recovery.table.headers.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => {
                  const isRecovering =
                    recoveryState[record.fileId]?.status === 'ongoing';
                  const isWaitingForServer =
                    recoveryState[record.fileId]?.status === 'waiting';
                  const progress = recoveryState[record.fileId]?.progress || 0;
                  const recordStatus = record.status || 'pending';
                  const recoveryCompleted =
                    recoveryState[record.fileId]?.status === 'success';

                  const getStatusLabel = () => {
                    if (recoveryState[record.fileId]?.status === 'error') {
                      return {
                        text: t('recovery.status.failed'),
                        color: 'error.main',
                      };
                    }

                    if (isWaitingForServer) {
                      return {
                        text: t(
                          'recovery.status.serverFinalizing',
                          'Server is still finalizing',
                        ),
                        color: 'info.main',
                      };
                    }

                    if (recoveryCompleted) {
                      if (recordStatus === 'recovered') {
                        return {
                          text: t('recovery.status.recovered'),
                          color: 'success.main',
                        };
                      }
                      return {
                        text: t('recovery.status.recoveryComplete'),
                        color: 'success.main',
                      };
                    }

                    switch (recordStatus) {
                      case 'uploaded':
                        return {
                          text: '',
                          color: 'info.main',
                        };
                      case 'recovered':
                        return {
                          text: t('recovery.status.recovered'),
                          color: 'success.main',
                        };
                      case 'dismissed':
                      case 'pending':
                      default:
                        return {
                          text: t('recovery.status.interruptedRecording'),
                          color: 'warning.main',
                        };
                    }
                  };

                  const statusLabel = getStatusLabel();
                  const canRecover =
                    !isRecovering && !recoveryCompleted && !isWaitingForServer;

                  return (
                    <TableRow key={record.fileId}>
                      <TableCell>{record.fileId}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(record.startedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <span style={{ color: statusLabel.color }}>
                          {statusLabel.text}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              disabled={isRecovering}
                            >
                              {isRecovering ? (
                                `${Math.round(progress)}%`
                              ) : (
                                <MoreHorizontal className="h-4 w-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canRecover && (
                              <DropdownMenuItem
                                onClick={() => handleRecover(record)}
                                disabled={isRecovering}
                              >
                                <ArchiveRestore className="mr-2 h-4 w-4" />
                                {t('recovery.actions.restore')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleDelete(record.fileId)}
                              disabled={isRecovering}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('recovery.actions.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </SettingsLayout>
  );
};

export default RecoveryPage;
